import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './Tabs.module.scss';

/* ────────────────────────────────────────────────────────────
 * Tabs — pure controlled tab strip primitive.
 *
 *   <Tabs
 *     activeKey={key}
 *     onChange={setKey}
 *     variant="line"
 *     items={[
 *       { key: 'personal', label: 'Personal Details' },
 *       { key: 'contact',  label: 'Contact Details'  },
 *     ]}
 *   />
 *
 * Variants:
 *   - "line"      sliding-underline indicator (default — used by
 *                 every page-level tab nav including BaseMenu's
 *                 7 sub-tabs).
 *   - "pill"      Linear-style filled pill (good for sub-filters
 *                 nested inside a card).
 *   - "segmented" iOS-style joined buttons (good for binary or
 *                 ternary mode switches).
 *
 * The strip is pure — the consumer renders matching content next
 * to / below it based on `activeKey`.
 * ──────────────────────────────────────────────────────────── */

const VARIANT_CLASS = {
  line: styles.variantLine,
  pill: styles.variantPill,
  segmented: styles.variantSegmented,
};

const Tabs = ({ activeKey, onChange, items, variant, className, ariaLabel, size }) => {
  const stripRef = useRef(null);
  const tabRefs = useRef({});
  const [indicator, setIndicator] = useState({ left: 0, width: 0, visible: false });

  const measureIndicator = useCallback(() => {
    if (variant !== 'line') return;
    const node = tabRefs.current[activeKey];
    const strip = stripRef.current;
    if (!node || !strip) return;
    const stripRect = strip.getBoundingClientRect();
    const nodeRect = node.getBoundingClientRect();
    setIndicator({
      left: nodeRect.left - stripRect.left + strip.scrollLeft,
      width: nodeRect.width,
      visible: true,
    });
  }, [activeKey, variant]);

  // useLayoutEffect prevents a one-frame flash where the indicator
  // is in its old position after a tab change.
  useLayoutEffect(() => {
    measureIndicator();
  }, [measureIndicator, items]);

  useEffect(() => {
    if (variant !== 'line' || typeof window === 'undefined') return undefined;
    const ro = new ResizeObserver(() => measureIndicator());
    if (stripRef.current) ro.observe(stripRef.current);
    window.addEventListener('resize', measureIndicator);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measureIndicator);
    };
  }, [measureIndicator, variant]);

  const stripClass = [
    styles.tabs,
    VARIANT_CLASS[variant] || styles.variantPill,
    size === 'sm' ? styles.sizeSm : '',
    size === 'lg' ? styles.sizeLg : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div role="tablist" aria-label={ariaLabel} className={stripClass} ref={stripRef}>
      {items.map((item) => {
        const isActive = item.key === activeKey;
        const tabClass = [
          styles.tab,
          isActive ? styles.tabActive : '',
          item.disabled ? styles.tabDisabled : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <button
            key={item.key}
            ref={(el) => { tabRefs.current[item.key] = el; }}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-disabled={item.disabled || undefined}
            disabled={item.disabled}
            className={tabClass}
            onClick={() => !item.disabled && onChange(item.key)}
          >
            {item.icon && <span className={styles.tabIcon} aria-hidden="true">{item.icon}</span>}
            <span className={styles.tabLabel}>{item.label}</span>
            {typeof item.count === 'number' && (
              <span className={styles.tabCount}>{item.count}</span>
            )}
          </button>
        );
      })}

      {variant === 'line' && (
        <span
          className={styles.indicator}
          aria-hidden="true"
          style={{
            transform: `translateX(${indicator.left}px)`,
            width: indicator.width,
            opacity: indicator.visible ? 1 : 0,
          }}
        />
      )}
    </div>
  );
};

Tabs.propTypes = {
  activeKey: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      icon: PropTypes.node,
      count: PropTypes.number,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  variant: PropTypes.oneOf(['line', 'pill', 'segmented']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

Tabs.defaultProps = {
  variant: 'line',
  size: 'md',
  className: '',
  ariaLabel: undefined,
};

export default Tabs;
